// autopilot-control-directive-commands.ts
//
// Registers the "control" subcommands (approve/pause/resume/redirect/revert/
// abort) — split out of commands/autopilot.ts to keep both files ≤200 lines.
// Every one of these writes ONLY to the inbox via directive-inbox.ts — NEVER
// touches mission.json directly (spec §3.3 Req 4-7, tests 25-29).
//
// M1 (code-reviewer 260916 phase-04): moved out, no behaviour change: shared
// deps type + inbox writer -> autopilot-control-directive-shared.ts; `answer`
// -> autopilot-answer-directive-command.ts; phase-04 [N4] `abort` two-step
// challenge -> autopilot-abort-challenge-command.ts.
import type { Command } from "commander";
import { requireFlag } from "../non-interactive-context.js";
import { log } from "../terminal-logger.js";
import { registerAutopilotAbortChallengeCommand } from "./autopilot-abort-challenge-command.js";
import { registerAutopilotAnswerDirectiveCommand } from "./autopilot-answer-directive-command.js";
import {
  type ControlDirectiveDeps,
  writeControlDirective,
} from "./autopilot-control-directive-shared.js";
import { REVERT_NOT_SUPPORTED_V1_MESSAGE } from "./ticket-v1-runnability.js";

// Re-exported so existing importers of this module keep resolving the type
// from its original path (the split is internal, not a contract change).
export type { ControlDirectiveDeps } from "./autopilot-control-directive-shared.js";

export function registerAutopilotControlDirectiveCommands(
  autopilot: Command,
  deps: ControlDirectiveDeps,
): void {
  autopilot
    .command("approve <gate>")
    .description("Duyệt gate qua directive local")
    .option("--mission <id>", "Mission ID")
    .option("--request <id>", "Request ID của gate")
    // E1/H1: phạm vi quyền người duyệt cấp. Không khai ⇒ gate không bị siết
    // (hành vi cũ). Chỉ có ý nghĩa với g3 (deploy) — gate khác bỏ qua.
    .option("--targets <list>", "G3: danh sách môi trường được phép, phân tách bằng dấu phẩy")
    .option("--expires <iso>", "G3: thời điểm hết hạn quyền deploy (ISO 8601)")
    .option("--repo <path>", "Repo path để resolve mission")
    .option("--json", "Output JSON")
    .action(
      async (
        gate: string,
        opts: {
          mission?: string;
          request?: string;
          targets?: string;
          expires?: string;
          repo?: string;
          json?: boolean;
        },
      ) => {
        const requestId = requireFlag(opts.request, {
          flag: "--request",
          command: "autopilot approve",
          hint: "--request <id>",
        });
        const workspaceRoot = await deps.resolveWorkspaceRoot(opts.repo);
        const targets = opts.targets
          ?.split(",")
          .map((t) => t.trim())
          .filter((t) => t.length > 0);
        await writeControlDirective(
          deps,
          workspaceRoot,
          opts.mission,
          "approve",
          {
            gate,
            request_id: requestId,
            ...(targets !== undefined && targets.length > 0 ? { targets } : {}),
            ...(opts.expires !== undefined ? { expires_at: opts.expires } : {}),
          },
          !!opts.json,
        );
      },
    );

  autopilot
    .command("pause")
    .description("Dừng nhận việc mới sau attempt hiện tại")
    .option("--mission <id>", "Mission ID")
    .option("--repo <path>", "Repo path để resolve mission")
    .option("--json", "Output JSON")
    .action(async (opts: { mission?: string; repo?: string; json?: boolean }) => {
      const workspaceRoot = await deps.resolveWorkspaceRoot(opts.repo);
      await writeControlDirective(
        deps,
        workspaceRoot,
        opts.mission,
        "pause",
        { requested_at: new Date().toISOString() },
        !!opts.json,
      );
    });

  autopilot
    .command("resume")
    .description("Tiếp tục, giữ điều kiện gate/quota")
    .option("--mission <id>", "Mission ID")
    .option("--repo <path>", "Repo path để resolve mission")
    .option("--json", "Output JSON")
    .action(async (opts: { mission?: string; repo?: string; json?: boolean }) => {
      const workspaceRoot = await deps.resolveWorkspaceRoot(opts.repo);
      await writeControlDirective(
        deps,
        workspaceRoot,
        opts.mission,
        "resume",
        { requested_at: new Date().toISOString() },
        !!opts.json,
      );
    });

  // B4/M1: lệnh `answer` ở file riêng (hai hợp đồng: câu hỏi interview qua
  // `--question`, và dạng cũ `<ticket> --request`).
  registerAutopilotAnswerDirectiveCommand(autopilot, deps);

  autopilot
    .command("redirect")
    .description("Tạo directive redirect (chỉ đạo mới)")
    .option("--mission <id>", "Mission ID")
    .option("--text <text>", "Nội dung redirect")
    .option("--repo <path>", "Repo path để resolve mission")
    .option("--json", "Output JSON")
    .action(async (opts: { mission?: string; text?: string; repo?: string; json?: boolean }) => {
      const text = requireFlag(opts.text, {
        flag: "--text",
        command: "autopilot redirect",
        hint: "--text <text>",
      });
      const workspaceRoot = await deps.resolveWorkspaceRoot(opts.repo);
      await writeControlDirective(
        deps,
        workspaceRoot,
        opts.mission,
        "redirect",
        { text },
        !!opts.json,
      );
    });

  // Req 4 (phase 08, D3): revert-v1 is a controller executor with no
  // dispatch path in v1 (N1: the old CLI wrote a directive nothing ever
  // consumed — a silent no-op). Rejection is the FIRST thing the action
  // does — BEFORE deps.resolveWorkspaceRoot — so a caller with no valid
  // workspace still gets the same clear answer instead of an unrelated
  // resolve error (H4). `.option`s stay declared (including `--repo`, added
  // by phase 01) so `--help` output is unchanged.
  autopilot
    .command("revert <ticket>")
    .description("Yêu cầu revert một ticket (chưa hỗ trợ ở v1)")
    .option("--mission <id>", "Mission ID")
    .option("--repo <path>", "Repo path để resolve mission")
    .option("--json", "Output JSON")
    .action((_ticket: string, _opts: { mission?: string; repo?: string; json?: boolean }) => {
      log.error(REVERT_NOT_SUPPORTED_V1_MESSAGE);
      process.exitCode = 1;
    });

  // [N4] Two-step abort challenge — file riêng (B2/B8: bước 1 phải ghi
  // directive mang challenge_id + in đúng 1 dòng JSON, khác hẳn hợp đồng
  // writeControlDirective dùng cho các lệnh khác).
  registerAutopilotAbortChallengeCommand(autopilot, deps);
}
